import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import SearchSelect from '../Global/SearchSelect';
import Checkbox from '../Global/Checkbox';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import {
    setCardCustomFieldValue,
    clearCardCustomFieldValue,
    selectBoardCustomFields,
    selectCardCustomFieldValues
} from '../../redux/Slices/customFieldsSlice';
import { showError } from '../../utils/snackbarHelpers';

const TYPE_ICON = {
    text: 'short_text',
    number: 'tag',
    date: 'calendar_month',
    dropdown: 'arrow_drop_down_circle',
    checkbox: 'check_box'
};

/* ---- Date trigger ---- */
// Renders a button styled like the other value inputs; clicking it opens the
// `cardCustomFieldDate` popover from the app's popover registry, which
// reads/writes the value via Redux on its own.

const DateValue = ({ cardId, field, value, onClear, isReadOnly }) => {
    const { handlePopoverClick } = useHandlePopoverClick();
    const date = value?.dateValue ? new Date(value.dateValue) : null;
    const label = date ? format(date, 'MMM d, yyyy') : 'Pick a date';
    const triggerId = `popover-cf-date-${field._id}`;

    return (
        <button
            id={triggerId}
            type="button"
            className={`cf-value-input cf-date-trigger ${date ? '' : 'placeholder'}`}
            disabled={isReadOnly}
            data-popover-trigger
            onClick={(e) => handlePopoverClick(e, 'cardCustomFieldDate', {
                cardId,
                fieldId: field._id
            })}
        >
            <span className="material-symbols-outlined cf-date-icon">calendar_month</span>
            <span className="cf-date-text">{label}</span>
            {date && !isReadOnly && (
                <span
                    className="material-symbols-outlined cf-date-clear"
                    role="button"
                    aria-label="Clear date"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                >close</span>
            )}
        </button>
    );
};

/* ---- Per-type editor ---- */

const FieldValueEditor = ({ cardId, field, value, onCommit, onClear, isReadOnly }) => {
    if (field.type === 'text') {
        return (
            <input
                type="text"
                className="cf-value-input"
                disabled={isReadOnly}
                defaultValue={value?.textValue || ''}
                placeholder="Add value"
                maxLength={500}
                onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next === (value?.textValue || '')) return;
                    if (next === '') onClear();
                    else onCommit(next);
                }}
            />
        );
    }
    if (field.type === 'number') {
        const { min, max, unit } = field.config || {};
        // Build a placeholder/hint that surfaces the allowed range so users
        // know the constraints before the server bounces them.
        let rangeHint = '';
        if (min != null && max != null) rangeHint = `${min}–${max}`;
        else if (min != null) rangeHint = `≥ ${min}`;
        else if (max != null) rangeHint = `≤ ${max}`;
        const placeholder = [rangeHint, unit].filter(Boolean).join(' ') || 'Number';
        return (
            <input
                type="number"
                className="cf-value-input"
                disabled={isReadOnly}
                defaultValue={value?.numberValue ?? ''}
                placeholder={placeholder}
                min={min != null ? min : undefined}
                max={max != null ? max : undefined}
                onBlur={(e) => {
                    if (e.target.value === '') return onClear();
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next) || next === value?.numberValue) return;
                    onCommit(next);
                }}
            />
        );
    }
    if (field.type === 'date') {
        return (
            <DateValue cardId={cardId} field={field} value={value} onClear={onClear} isReadOnly={isReadOnly} />
        );
    }
    if (field.type === 'dropdown') {
        const options = (field.options || [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(opt => ({ value: opt._id, label: opt.label, color: opt.color }));
        return (
            <SearchSelect
                options={options}
                placeholder="Select an option"
                defaultValue={value?.optionId}
                isDisabled={isReadOnly}
                onSelect={(opt) => {
                    if (!opt) onClear();
                    else onCommit(opt.value);
                }}
            />
        );
    }
    if (field.type === 'checkbox') {
        return (
            <Checkbox
                checked={!!value?.booleanValue}
                onChange={(checked) => onCommit(checked)}
                disabled={isReadOnly}
            />
        );
    }
    return null;
};

/* ---- Section ---- */

const CardCustomFields = ({ cardId, boardId, isReadOnly = false }) => {
    const dispatch = useDispatch();
    const fields = useSelector(state => selectBoardCustomFields(state, boardId));
    const values = useSelector(state => selectCardCustomFieldValues(state, cardId));

    const valueByFieldId = useMemo(() => {
        const map = {};
        values.forEach(v => { map[String(v.fieldId)] = v; });
        return map;
    }, [values]);

    if (!fields.length) return null;

    const sortedFields = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const handleSet = async (fieldId, value) => {
        const result = await dispatch(setCardCustomFieldValue({ cardId, fieldId, value }));
        // createAsyncThunk + rejectWithValue stashes the actual message on
        // result.payload; result.error.message is just 'Rejected'. Prefer payload.
        if (result.error) showError(dispatch, result.payload || result.error?.message || 'Failed to update field');
    };
    const handleClear = async (fieldId) => {
        const result = await dispatch(clearCardCustomFieldValue({ cardId, fieldId }));
        if (result.error) showError(dispatch, result.payload || result.error?.message || 'Failed to clear field');
    };

    return (
        <div className="card-custom-fields">
            <div className="card-section-header">
                <h3>
                    <span className="material-symbols-outlined">tune</span>
                    Custom fields
                </h3>
            </div>
            <div className="cf-blocks">
                {sortedFields.map(field => (
                    <div key={field._id} className="cf-block">
                        <div className="cf-block-name">
                            <span className="material-symbols-outlined cf-block-icon">
                                {TYPE_ICON[field.type] || 'tune'}
                            </span>
                            <span className="cf-block-label">{field.name}</span>
                            {field.config?.unit && <span className="cf-unit">({field.config.unit})</span>}
                        </div>
                        <div className="cf-block-input">
                            <FieldValueEditor
                                cardId={cardId}
                                field={field}
                                value={valueByFieldId[String(field._id)]}
                                onCommit={(v) => handleSet(field._id, v)}
                                onClear={() => handleClear(field._id)}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CardCustomFields;
